import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Collapse,
} from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
} from "@mui/icons-material";
import { CollapsibleSection } from "../../../components/CollapsibleSection";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import type {
  ReminderWithAction,
  GetMyRemindersResponse,
} from "@services/emailAutomationPipeline/types";
import { ActionCard } from "./ActionCard";
import { EmailPreview } from "./EmailPreview";
import { useShipmentActions } from "./useShipmentActions";

interface ShipmentActionsComponentProps {
  shipmentId: string;
}

export const ShipmentActionsComponent: React.FC<ShipmentActionsComponentProps> = ({
  shipmentId,
}) => {
  const { apiClient } = useOutlookPlugin();
  const [reminders, setReminders] = useState<ReminderWithAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const {
    draftPreview,
    setDraftPreview,
    draftPreviewAction,
    handleExecute,
    handleSend,
    handleAddAttachment,
    handleRemoveAttachment,
    handleResolve,
  } = useShipmentActions(apiClient, setReminders, { removeOnComplete: false });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<GetMyRemindersResponse>(
          `/email-automation/actions?shipmentId=${encodeURIComponent(shipmentId)}`
        );
        setReminders(response.reminders);
      } catch (err) {
        console.error("Error fetching shipment actions:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shipmentId, apiClient]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const activeReminders = reminders.filter((r) => r.reminderStatus !== "resolved");
  const resolvedReminders = reminders.filter(
    (r) => r.reminderStatus === "resolved" && r.resolutionType === "user"
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        position: "relative",
      }}
    >
      <CollapsibleSection
        title="Actions"
        count={activeReminders.length}
        expanded={open}
        onToggle={() => setOpen(!open)}
      >
        <Box sx={{ px: 1.5, pb: 1 }}>
          {draftPreview && draftPreviewAction && (
            <EmailPreview
              mode="draft"
              action={draftPreviewAction}
              draftMessageId={draftPreview.draftMessageId}
              draftTo={draftPreview.to}
              draftCc={draftPreview.cc}
              draftSubject={draftPreview.subject}
              draftAttachments={draftPreview.attachments}
              missingAttachments={draftPreview.missingAttachments}
              shipmentId={shipmentId}
              onAddAttachment={(attId) => handleAddAttachment(draftPreview.reminder.actionId, attId)}
              onRemoveAttachment={(attId) => handleRemoveAttachment(draftPreview.reminder.actionId, attId)}
              replyToEmailId={draftPreview.reminder.data.replyToEmailId as string | undefined}
              onSend={() => handleSend(draftPreview.reminder.actionId)}
              onDismiss={() => setDraftPreview(null)}
            />
          )}

          {activeReminders.length === 0 && !showResolved && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No active actions for this shipment.
            </Typography>
          )}

          {activeReminders.map((reminder) => (
            <ActionCard
              key={reminder.actionId}
              reminder={reminder}
              onExecute={() => handleExecute(reminder)}
              onResolve={reminder.reminderId ? () => handleResolve(reminder) : undefined}
            />
          ))}

          {resolvedReminders.length > 0 && (
            <>
              <Button
                size="small"
                startIcon={<ResolveIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => setShowResolved(!showResolved)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: "#1565C0",
                  mb: 0.5,
                  px: 0.5,
                  "& .MuiButton-startIcon": { mr: 0.3 },
                }}
              >
                {showResolved
                  ? "Hide resolved actions"
                  : `Show ${resolvedReminders.length} resolved action${resolvedReminders.length > 1 ? "s" : ""}`}
              </Button>
              <Collapse in={showResolved}>
                {resolvedReminders.map((reminder) => (
                  <ActionCard
                    key={reminder.actionId}
                    reminder={reminder}
                    onExecute={() => handleExecute(reminder)}
                    hideButton
                  />
                ))}
              </Collapse>
            </>
          )}
        </Box>
      </CollapsibleSection>
    </Box>
  );
};
